/**
 * Audio I/O — TTS over HTTP, push-to-talk ASR over WebSocket.
 *
 * Both endpoints sit on top of sherpa-onnx running locally in the
 * FastAPI backend. The first call after install triggers a one-time
 * model download into ~/.nbyula-cowork/models/.
 *
 * The ASR socket is push-to-talk: the client streams 16 kHz mono PCM
 * frames while the user holds/toggles the mic, then sends ``{type:"end"}``
 * to request decode. The backend (offline Moonshine) replies with a
 * single ``{type:"final"}`` frame and closes. There are no live
 * partials — the UI renders a microphone-level waveform during recording
 * to compensate.
 */

import { getAccessToken } from "./client";

export type AudioPhase =
  | "idle"
  | "downloading"
  | "extracting"
  | "initializing"
  | "ready"
  | "error";

export interface EngineStatus {
  installed: boolean;
  running: boolean;
  phase: AudioPhase;
  bytes: number;
  total: number;
  error: string | null;
}

export interface AudioStatus {
  asr: EngineStatus;
  tts: EngineStatus;
  /**
   * Whether the Python `sherpa-onnx` package can actually be imported
   * in the backend process. False on a stale dev venv that predates
   * the dependency being added to ``pyproject.toml``; the modal uses
   * this to show a remediation hint instead of pretending the model
   * is usable.
   */
  backend_available: boolean;
  /** Specific import-error message (e.g. ABI mismatch) when not available. */
  backend_error: string | null;
}

export type AudioEngine = "asr" | "tts";

/** Snapshot of bundle presence + live install progress (polled by the modal). */
export async function fetchAudioStatus(): Promise<AudioStatus> {
  const res = await fetch("/api/audio/status");
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
}

/**
 * Kick off (or join) a background install for ``engine``. Returns
 * immediately — the actual download is tracked via :func:`fetchAudioStatus`.
 * Throws on 503 when sherpa-onnx isn't installed on the backend.
 */
export async function installAudioEngine(engine: AudioEngine): Promise<void> {
  const res = await fetch("/api/audio/install", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ engine }),
  });
  if (!res.ok) {
    let detail = `install failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* keep generic */
    }
    throw new Error(detail);
  }
}

// Mirrors ws.ts — use the page's own host (cloud / dev proxy / desktop) and
// fall back to the loopback default only when location.host is empty.
const _host =
  typeof window !== "undefined" && window.location.host
    ? window.location.host
    : "127.0.0.1:19532";
const WS_BASE = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${_host}`;

/**
 * Fetch a WAV blob for the given text. Returns a fresh object URL on each
 * call — callers are responsible for revoking it when the <audio> element
 * is unmounted or replaced.
 */
export async function fetchTtsBlobUrl(text: string): Promise<string> {
  const res = await fetch("/api/audio/tts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    let detail = `TTS failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* not JSON — keep the generic message */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export interface DictationEvents {
  /** Recognizer is loaded and ready to receive audio. */
  onReady?: () => void;
  /**
   * Final transcript for the just-decoded utterance. Fires once per
   * session, right before ``onClose``. ``truncated`` is true when the
   * recording hit the backend's MAX_UTTERANCE_SECONDS cap.
   */
  onFinal?: (text: string, truncated: boolean) => void;
  /** Connection closed (either side). */
  onClose?: () => void;
  /** Backend reported an error (sherpa-onnx missing, model load failed, …). */
  onError?: (message: string) => void;
}

/**
 * Context the client attaches to the ``end`` frame so the backend's
 * refinement pass can integrate the new dictation into what's already in
 * the box and resolve names/casing from recent chat. All fields optional.
 */
export interface DictationEndContext {
  /** Text already in the input box; the refiner appends to it (and can
   *  edit it via spoken commands like "delete the last word"). */
  draft?: string;
  /** Last few chat turns, newest last — context only, never acted on. */
  recent?: Array<{ role: string; text: string }>;
}

export interface DictationSocket {
  /** Send a binary PCM16 chunk (LE, 16 kHz mono). */
  sendPcm: (chunk: ArrayBuffer) => void;
  /** Tell the backend "I'm done" — flushes the tail; final + close follow.
   *  Optionally attach draft/recent context for the refinement pass. */
  end: (context?: DictationEndContext) => void;
  /** Hard-close without waiting for a flush. */
  close: () => void;
  /** True once the server has sent "ready". */
  isReady: () => boolean;
}

/**
 * Open the ASR WebSocket. The caller is expected to pipe PCM via
 * ``sendPcm`` and then call ``end`` (when the user stops the mic).
 */
export function openDictationSocket(events: DictationEvents): DictationSocket {
  const token = getAccessToken();
  if (!token) {
    events.onError?.("Not signed in");
    return {
      sendPcm: () => {},
      end: () => {},
      close: () => {},
      isReady: () => false,
    };
  }

  const ws = new WebSocket(`${WS_BASE}/ws/asr?token=${encodeURIComponent(token)}`);
  ws.binaryType = "arraybuffer";
  let ready = false;
  // Audio fed before "ready" arrives is buffered so the user's first
  // half-second of speech isn't dropped while the model warms up.
  const preReadyBuffer: ArrayBuffer[] = [];
  let endRequested = false;
  // Context to attach to the "end" frame. Captured at end() time and also
  // used by the deferred-end path (when end() is called before "ready").
  let endContext: DictationEndContext = {};

  const endFrame = (): string =>
    JSON.stringify({
      type: "end",
      draft: endContext.draft ?? "",
      recent: endContext.recent ?? [],
    });
  // Per-session counters mirror the backend's log so the two sides can be
  // diffed when the transcript turns out empty or the WS closes early.
  let chunksSent = 0;
  let bytesSent = 0;

  ws.onopen = () => {
    console.debug("[dictation] ws: open");
  };

  ws.onmessage = (ev) => {
    if (typeof ev.data !== "string") return;
    let msg: { type: string; text?: string; message?: string; truncated?: boolean };
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }
    switch (msg.type) {
      case "ready":
        ready = true;
        console.debug(
          "[dictation] ws: ready (flushing %d pre-ready chunks)",
          preReadyBuffer.length,
        );
        events.onReady?.();
        for (const chunk of preReadyBuffer) {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(chunk);
            chunksSent += 1;
            bytesSent += chunk.byteLength;
          }
        }
        preReadyBuffer.length = 0;
        if (endRequested && ws.readyState === WebSocket.OPEN) {
          console.debug("[dictation] ws: send end (deferred — was queued before ready)");
          ws.send(endFrame());
        }
        break;
      case "final":
        // Final fires even for an empty transcript (silence / aborted
        // utterance) so the hook can transition out of "finalizing".
        console.debug(
          "[dictation] ws: final (text_len=%d, truncated=%s, sent_chunks=%d, sent_bytes=%d)",
          (msg.text ?? "").length, msg.truncated === true, chunksSent, bytesSent,
        );
        events.onFinal?.(msg.text ?? "", msg.truncated === true);
        break;
      case "error":
        console.warn("[dictation] ws: error frame —", msg.message);
        events.onError?.(msg.message ?? "Unknown ASR error");
        break;
    }
  };

  ws.onerror = (ev) => {
    console.warn("[dictation] ws: onerror", ev);
    events.onError?.("WebSocket error");
  };
  ws.onclose = (ev) => {
    console.debug(
      "[dictation] ws: close (code=%d, reason=%s, sent_chunks=%d, sent_bytes=%d)",
      ev.code, ev.reason || "(none)", chunksSent, bytesSent,
    );
    events.onClose?.();
  };

  return {
    sendPcm: (chunk) => {
      if (!ready) {
        preReadyBuffer.push(chunk);
        return;
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(chunk);
        chunksSent += 1;
        bytesSent += chunk.byteLength;
      }
    },
    end: (context?: DictationEndContext) => {
      endRequested = true;
      endContext = context ?? {};
      if (ready && ws.readyState === WebSocket.OPEN) {
        console.debug(
          "[dictation] ws: send end (chunks=%d, bytes=%d, draft_len=%d, recent=%d)",
          chunksSent, bytesSent,
          (endContext.draft ?? "").length, (endContext.recent ?? []).length,
        );
        ws.send(endFrame());
      } else {
        console.debug(
          "[dictation] ws: end requested before ready — will flush on ready (ws.readyState=%d)",
          ws.readyState,
        );
      }
    },
    close: () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        console.debug(
          "[dictation] ws: hard close (chunks=%d, bytes=%d)",
          chunksSent, bytesSent,
        );
        ws.close();
      }
    },
    isReady: () => ready,
  };
}
